import {
  type HubAlgorithm,
  HubBuildState,
  type HubExampleDefinition,
  RequestParams,
} from '@robovision/hub'
import {
  addOrReplaceBy,
  defaultSort,
  flatten,
  groupBy,
  HttpStatusCode,
  naturalSort,
  sortByKey,
} from '@robovision/rvai-utils'
import { useIsFetching } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import {
  coerce,
  gt,
  rcompare,
} from 'semver'
import {
  computed,
  reactive,
  toRefs,
} from 'vue'
import { Algorithm } from '~domain/algorithm/Algorithm'
import { AlgorithmDefinition } from '~domain/algorithm/AlgorithmDefinition'
import type { JobV2 } from '~domain/jobV2/Job'
import { hubApi } from '~services/api'
import { queryClient } from '~services/query-client'
import {
  acceptStatus,
} from '~utils/api'
import { RETHROW_ERROR_AFTER_HANDLING } from '~utils/api/error-handling'


function getAlgorithmDefinitionFromApi ({
  id, version,
}: {
  id: string
  version: string
}) {
  return queryClient.fetchQuery({
    queryFn: () => hubApi.algorithms.getAlgorithmDefinition(
      id,
      version,
      { original: false },
      {
        ...RETHROW_ERROR_AFTER_HANDLING,
        validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND),
      },
    ),
    queryKey: ['hubApi', 'aglorithms', id, version, 'definition'],
    staleTime: query => query.state.error != null ? 0 : Infinity,
  })
}

function getAlgorithmFromApi ({
  id, version,
}: {
  id: string
  version: string
}) {
  return queryClient.fetchQuery({
    queryFn: () => hubApi.algorithms.getAlgorithm(id, version, RETHROW_ERROR_AFTER_HANDLING),
    queryKey: ['hubApi', 'algorithms', id, version],
    staleTime: query => query.state.error != null ? 0 : Infinity,
  })
}

type GetAlgorithmsQuery = Parameters<(typeof hubApi.algorithms.listAlgorithms)>[0]

function getAlgorithmsFromApi (payload: GetAlgorithmsQuery) {
  return queryClient.fetchQuery({
    queryFn: () => {
      const params: RequestParams = {
        ...RETHROW_ERROR_AFTER_HANDLING,
      }

      if (window.Cypress) {
        // On CI there is a rare bug in connection between frontend and backend.
        // It causes the request to never complete.
        // Therefore we want to setup a timeout and retry policy to handle this case.
        // Failed job: https://git.robovision.ai/RoboVision/rvai/-/jobs/5177468
        // We set it to 8s to be able to retry 2 times within the cypress timeout of 30s.
        params.timeout = 8_000
        params['axios-retry'] = {
          retries: 2,
          shouldResetTimeout: true,
          retryCondition: (error) => {
            return error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK
          },
          retryDelay: () => 1000, // 1s
          onRetry: (retryCount, error) => {
            console.warn(`Axios Retry #${retryCount}. Error: ${error.message}`)
          },
        }
      }

      return hubApi.algorithms.listAlgorithms(
        {
          includeDeleted: true,
          includeExtensions: true,
        },
        params,
      )
    },
    queryKey: ['hubApi', 'algorithms', payload],
  })
}

export const useAlgorithmsStore = defineStore('algorithms', () => {
  const state = reactive({
    algorithmDefinitions: [],
    algorithmExtensions: [],
    algorithms: [],
    isAlgorithmsDefinitionsLoaded: false,
    isAlgorithmsLoaded: false,
  }) as {
    algorithmDefinitions: AlgorithmDefinition[]
    algorithmExtensions: Algorithm[]
    algorithms: Algorithm[]
    isAlgorithmsDefinitionsLoaded: boolean
    isAlgorithmsLoaded: boolean
  }

  const algorithmsLoading = useIsFetching(
    {
      queryKey: ['hubApi', 'algorithms'],
    },
    queryClient,
  )
  const loading = computed(() => algorithmsLoading.value > 0)

  const getters = {
    getAlgorithm (id: string | undefined, version: string | undefined): Algorithm | undefined {
      if (!id || !version) return undefined
      return state.algorithms
        .find(a => a.id === id && a.version === version)
    },

    getAlgorithmDefinition (id: string | undefined, version: string | undefined): AlgorithmDefinition | undefined {
      if (!id || !version) return undefined
      return state.algorithmDefinitions
        .find(a => a.id === id && a.version === version)
    },

    getAlgorithmDefinitionByJob (job: JobV2): AlgorithmDefinition | undefined {
      return state.algorithmDefinitions
        .find(a => a.id === job.algorithmId && a.version === job.algorithmVersion)
    },

    getAlgorithmLatestVersion (id: string): string | undefined {
      return state.algorithms
        .filter(a => a.id === id)
        .map(a => a.version)
        .sort(naturalSort)
        .pop()
    },

    getAllAlgorithmDefinitions (): AlgorithmDefinition[] {
      return state.algorithmDefinitions
    },

    getAllExampleDefinitions (): HubExampleDefinition[] {
      return flatten(state.algorithmDefinitions.map(a => a.exampleDefinitions as HubExampleDefinition[]))
    },

    getAvailableAlgorithms (): Algorithm[] {
      // only show algorithms that have at least 1 example definitions
      const filtered = state.algorithms
        .filter(algorithm => {
          return (
            algorithm.buildState === HubBuildState.READY &&
            this.getAlgorithmDefinition(algorithm.id, algorithm.version)?.exampleDefinitions?.length
          )
        })

      const grouped = groupBy(filtered, 'id')

      // get the latest version of which algorithm
      const latests = [...grouped.values()]
        .map(arr => {
          return arr.sort((a, b) => {
            const aVer = coerce(a.version)
            const bVer = coerce(b.version)

            // invalid versions are last
            if (aVer == null && bVer == null) return 0
            if (aVer == null) return 1
            if (bVer == null) return -1

            return rcompare(aVer, bVer)
          })[0]
        })
        .filter(Boolean)

      return sortByKey(latests, 'name')
    },

    getInstalledAlgorithm (id: string | undefined, version: string | undefined): Algorithm | undefined {
      if (!id || !version) return undefined
      return state.algorithms
        .concat(state.algorithmExtensions)
        .find(a => a.id === id && a.version === version)
    },

    getInstalledAlgorithms (): Algorithm[] {
      const algorithms = state.algorithms
        .concat(state.algorithmExtensions)
        .filter((a) => a.buildState !== HubBuildState.DELETED)
      return algorithms.sort((a, b) => {
        const nameSort = defaultSort(a.name, b.name)
        if (nameSort !== 0) return nameSort

        const aVer = coerce(a.version)
        const bVer = coerce(b.version)

        // invalid versions are last
        if (aVer == null && bVer == null) return 0
        if (aVer == null) return 1
        if (bVer == null) return -1

        return rcompare(aVer, bVer)
      })
    },

    isAlgorithmAvailable (id: string, version: string): boolean {
      const algorithm = state.algorithms.find((a) => a.id === id && a.version === version)
      return !!algorithm && algorithm.buildState === HubBuildState.READY
    },

    isInstalledAlgorithmAvailable (id: string, version: string): boolean {
      const algorithm = state.algorithms
        .concat(state.algorithmExtensions)
        .find((a) => a.id === id && a.version === version)
      return !!algorithm && algorithm.buildState === HubBuildState.READY
    },
  }

  const actions = {
    async deleteAlgorithm (id: string, version: string, deleteLocalState = true): Promise<void> {
      await hubApi.algorithms.deleteAlgorithm(id, version)

      if (deleteLocalState) {
        this.deleteAlgorithmLocalState(id, version)
      }
    },

    deleteAlgorithmLocalState (id: string, version: string): void {
      let index = state.algorithms.findIndex(a => a.id === id && a.version === version)
      if (index >= 0) {
        state.algorithms.splice(index, 1)
      }
      index = state.algorithmExtensions.findIndex(a => a.id === id && a.version === version)
      if (index >= 0) {
        state.algorithmExtensions.splice(index, 1)
      }
    },

    async isAlgorithmInstalled (id: string, version: string): Promise<boolean> {
      const response = await getAlgorithmDefinitionFromApi({
        id,
        version,
      })

      return response.status === HttpStatusCode.OK
    },

    async loadAlgorithm (id: string, version: string): Promise<Algorithm> {
      const algorithm = getters.getAlgorithm(id, version)
      if (algorithm) return algorithm

      const { data } = await getAlgorithmFromApi({
        id,
        version,
      })
      const newAlgorithm = Algorithm.fromResponse(data)

      if (data.extension) {
        state.algorithmExtensions = addOrReplaceBy(
          state.algorithmExtensions,
          newAlgorithm,
          a => a.id === id && a.version === version,
        )
      } else {
        state.algorithms = addOrReplaceBy(
          state.algorithms,
          newAlgorithm,
          a => a.id === id && a.version === version,
        )
      }

      return newAlgorithm
    },

    async loadAlgorithmDefinition ({
      force,
      id,
      skipInvalid,
      version,
    }: {
      force?: boolean
      id: string
      skipInvalid?: boolean
      version: string
    }): Promise<AlgorithmDefinition | undefined> {
      const definition = getters.getAlgorithmDefinition(id, version)
      if (definition && !force) return definition

      const response = await getAlgorithmDefinitionFromApi({
        id,
        version,
      })
      if (response.status === HttpStatusCode.NOT_FOUND) return undefined

      let algorithmDefinition: AlgorithmDefinition
      try {
        const data = response.data
        algorithmDefinition = AlgorithmDefinition.fromResponse(data)
      } catch (e) {
        if (skipInvalid) {
          console.log('Failed to load algorithm definition ' + id + '. Skipping')
          return undefined
        } else {
          throw e
        }
      }

      state.algorithmDefinitions = addOrReplaceBy(
        state.algorithmDefinitions,
        algorithmDefinition,
        a => a.key === algorithmDefinition.key,
      )

      return algorithmDefinition
    },

    async loadAlgorithms (): Promise<void> {
      const payload = {
        includeDeleted: true,
        includeExtensions: true,
      }
      const { data: algorithms } = await getAlgorithmsFromApi(payload)
      const latestAlgorithms = {} as Record<string, HubAlgorithm>

      for (const algorithm of algorithms.items) {
        if (!latestAlgorithms[algorithm.id]) {
          latestAlgorithms[algorithm.id] = algorithm
        } else {
          const latestVersion = coerce(latestAlgorithms[algorithm.id]?.version)
          const currentVersion = coerce(algorithm.version)

          if (latestVersion == null || currentVersion == null) continue

          if (!gt(latestVersion, currentVersion)) {
            latestAlgorithms[algorithm.id] = algorithm
          }
        }
      }

      // show the latest name, assuming it is the correct one
      const updatedAlgorithms = algorithms.items.map(a => ({
        ...a,
        name: latestAlgorithms[a.id]?.name ?? '',
      }))

      state.algorithms = updatedAlgorithms.filter(a => !a.extension).map(Algorithm.fromResponse)
      state.algorithmExtensions = updatedAlgorithms.filter(a => a.extension).map(Algorithm.fromResponse)
      state.isAlgorithmsLoaded = true
    },

    async loadAllAlgorithmDefinitions (force: boolean = false): Promise<void> {
      const algorithmPromises = state.algorithms
        .map(a => this.loadAlgorithmDefinition({
          force,
          id: a.id,
          version: a.version,
        }))
        .concat(state.algorithmExtensions.map(e => this.loadAlgorithmDefinition({
          force,
          id: e.id,
          skipInvalid: true,
          version: e.version,
        })))
      await Promise.all(algorithmPromises)
      state.isAlgorithmsDefinitionsLoaded = true
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },
  }

  return {
    ...toRefs(state),
    loading,
    ...actions,
    ...getters,
  }
})
